import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  billingsSlice,
  selectDeleteRecipientEmailStatus,
  selectUpdateCompanyStatus,
} from "./billingsSlice";
import { useEffect, useMemo, useState } from "react";
import {
  Spacer,
  PageDS,
  triggerToast,
  TabMenu,
  LaunchPage,
  ToastContainerDS,
  ArchiveOutlined,
  OpenInNewOutlined,
} from "@qivia/ui";
import {
  loadCompanyAsync,
  selectCompany,
  selectCompanyStatus,
  selectManager,
} from "./../homeSlice";
import { EmailsFrame, StatementTab } from "./archives/statementsTab";
import { useNavigate, useParams } from "react-router-dom";
import { InvoiceTab } from "./archives/invoicesTab";
import { AccountTab } from "./accountTab";
import { BankStatementTab } from "./archives/bankStatementsTab";
import {
  sepaDebitSlice,
  selectCreateExternalBankAccountStatus,
  selectCreateMemobankMandateStatus,
  getMemobankMandatePerCompanyAsync,
  selectIsMemobankMandateSent,
} from "./sepaDebitSlice";
import { SepaDebitModals } from "./modals/sepaDebitModals";
import { DepositTab } from "./archives/depositsTab";
import { AsfInvoicesTab } from "./archives/asfInvoicesTab";
import "../../../i18n/i18n";
import { selectManagersList } from "../users/usersSlice";
import { supportPhonesByCompanyGroupUuid } from "../../../constants/support";
import { BillingCompanyPortal } from "./v2/billingCompanyPortal";

export const Billings = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const [selectedTab, setSelectedTab] = useState<string>(
    params.tab ?? "account",
  );
  const company = useAppSelector(selectCompany);
  const manager = useAppSelector(selectManager);
  const updateCompanyStatus = useAppSelector(selectUpdateCompanyStatus);
  const deleteRecipientEmailStatus = useAppSelector(
    selectDeleteRecipientEmailStatus,
  );
  const isMemobankMandateSent = useAppSelector(selectIsMemobankMandateSent);
  const createExternalBankAccountStatus = useAppSelector(
    selectCreateExternalBankAccountStatus,
  );
  const companyStatus = useAppSelector(selectCompanyStatus);
  const createMemobankMandateStatus = useAppSelector(
    selectCreateMemobankMandateStatus,
  );
  const managers = useAppSelector(selectManagersList);

  const otherSupportPhones = useMemo(
    () =>
      managers && managers.length > 0 && managers[0].companyGroup
        ? supportPhonesByCompanyGroupUuid[managers[0].companyGroup]
        : undefined,
    [managers],
  );

  useEffect(() => {
    if (
      company &&
      companyStatus === "success" &&
      isMemobankMandateSent === null
    ) {
      void dispatch(
        getMemobankMandatePerCompanyAsync({ companyUuid: company.uuid }),
      );
    }
  }, [company, companyStatus, dispatch, isMemobankMandateSent]);

  const tabs = [
    {
      name: "account",
      title: t("billings.tab.account"),
    },
    {
      name: "invoices",
      title: t("billings.tab.invoices"),
    },
    {
      name: "statements",
      title: t("billings.tab.statements"),
    },
    {
      name: "bankStatements",
      title: t("billings.tab.bankStatements"),
    },
    {
      name: "deposits",
      title: t("billings.tab.deposits"),
    },
  ];

  const tabsWithBadges = [
    ...tabs,
    {
      name: "asfInvoices",
      title: t("billings.tab.asfInvoices"),
    },
  ];

  useEffect(() => {
    if (selectedTab && params.sepa_debit === undefined) {
      navigate(`/home/billings/${selectedTab}`);
    } else if (selectedTab) {
      navigate(
        `/home/billings/${selectedTab}${
          params.sepa_debit !== undefined ? `/${params.sepa_debit}` : "/"
        }`,
      );
    }
  }, [navigate, params.sepa_debit, selectedTab]);

  useEffect(() => {
    if (updateCompanyStatus === "success") {
      triggerToast(t("billings.emailsFrame.save.success") || "", "valid");
      if (company) {
        void dispatch(loadCompanyAsync({ uuid: company.uuid }));
        dispatch(billingsSlice.actions.resetUpdateCompanyStatus());
      }
    } else if (updateCompanyStatus === "failed") {
      triggerToast(t("billings.emailsFrame.save.error") || "", "error");
    }
  }, [company, dispatch, t, updateCompanyStatus]);

  useEffect(() => {
    if (deleteRecipientEmailStatus === "success") {
      triggerToast(t("billings.emailsFrame.delete.success") || "", "valid");
      if (company) {
        void dispatch(loadCompanyAsync({ uuid: company.uuid }));
        dispatch(billingsSlice.actions.resetDeleteRecipientEmailStatus());
      }
    } else if (deleteRecipientEmailStatus === "failed") {
      triggerToast(t("billings.emailsFrame.delete.error") || "", "error");
    }
  }, [company, deleteRecipientEmailStatus, dispatch, t]);

  useEffect(() => {
    if (createExternalBankAccountStatus === "success") {
      if (company) {
        void dispatch(loadCompanyAsync({ uuid: company.uuid }));
      }
      dispatch(sepaDebitSlice.actions.resetExternalBankAccountStatus());
      triggerToast(
        t("billings.sepaDebit.createExternalBankAccount.success") || "",
        "valid",
      );
    } else if (createExternalBankAccountStatus === "failed") {
      triggerToast(
        t("billings.sepaDebit.createExternalBankAccount.error") || "",
        "error",
      );
    }
  }, [company, createExternalBankAccountStatus, dispatch, t]);

  useEffect(() => {
    if (createMemobankMandateStatus === "success") {
      dispatch(sepaDebitSlice.actions.resetCreateMemobankMandateStatus());
      triggerToast(
        t("billings.sepaDebit.createMemobankMandate.success") || "",
        "valid",
      );
    } else if (createMemobankMandateStatus === "failed") {
      dispatch(sepaDebitSlice.actions.setMemobankMandateSent(false));
      triggerToast(
        t("billings.sepaDebit.createMemobankMandate.error") || "",
        "error",
      );
    }
  }, [company, createMemobankMandateStatus, dispatch, t]);

  if (!company || !manager) {
    return;
  }

  if (createExternalBankAccountStatus === "processing") {
    return <LaunchPage />;
  }

  const isSepaModalsOpen = params.sepa_debit !== undefined;

  const isInBillingPortalTab = params.tab === "invoices_portal";

  const billingPortalHeaderActions = isInBillingPortalTab
    ? {
        cta: {
          title: t("billings.portal.cta.portal"),
          action: () => {
            console.log("todo portal");
          },
          leftIcon: <OpenInNewOutlined />,
        },
        cta2: {
          title: t("billings.portal.cta.archives"),
          action: () => {
            console.log("todo archives");
          },
          leftIcon: <ArchiveOutlined />,
        },
      }
    : {};

  return (
    <PageDS
      title={t("billings.title")}
      toaster={<ToastContainerDS />}
      cta={billingPortalHeaderActions.cta}
      cta2={billingPortalHeaderActions.cta2}
      topElement={
        <>
          <TabMenu
            tabs={company.canOrderBadges ? tabsWithBadges : tabs}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
          />
          <Spacer y={0.5} />
          {["statements", "invoices", "bankStatements"].includes(
            selectedTab,
          ) && (
            <>
              <EmailsFrame company={company} />
              <Spacer y={2} />
            </>
          )}
        </>
      }
      withoutTopElement={isSepaModalsOpen}
      supportButton={{
        showMail: !otherSupportPhones,
        otherPhones: otherSupportPhones,
      }}
    >
      <>
        {!isSepaModalsOpen ? (
          <>
            <StatementTab />
            <InvoiceTab />
            <AccountTab />
            <BankStatementTab />
            <DepositTab />
            <AsfInvoicesTab />
            <BillingCompanyPortal />
          </>
        ) : (
          <SepaDebitModals
            company={company}
            manager={manager}
            params={params}
          />
        )}
      </>
    </PageDS>
  );
};
